import React from 'react';
import { Navigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

/**
 * Iter-211 — Legacy `/talent/{uuid}` deep-link guard.
 *
 * Prior to this release the `Open Full Profile` button (and a few
 * cached client emails/notifications) pointed at `/talent/{uuid}`, a
 * path that has never been mounted as a React Router route. Mobile
 * users landed on the generic "Page not found" screen.
 *
 * We can't change every old email or push-notification deep-link, so
 * this component is mounted at `/talent/:talentId` and silently
 * forwards to whichever canonical route the *current* user is allowed
 * to see:
 *
 *   admin / super_admin → /admin/talent/{id}
 *   client (any scope)  → /client/discover/{id}
 *   talent              → /talent/profile  (their own profile)
 *   not logged in       → /login?redirect=/talent/{id}
 *
 * The original UUID is preserved so the destination page can fetch
 * the right talent record. Scope enforcement still happens on the
 * destination route.
 */
const TalentProfileRedirect = () => {
  const { talentId } = useParams();
  const { user } = useAuth();
  const location = useLocation();
  const search = location.search || '';

  if (!user) {
    return <Navigate to={`/login?redirect=${encodeURIComponent(`/talent/${talentId}${search}`)}`} replace />;
  }
  const role = (user.role || '').toLowerCase();
  if (role === 'admin' || role === 'super_admin') {
    return <Navigate to={`/admin/talent/${talentId}${search}`} replace />;
  }
  if (role === 'client' || role === 'agency') {
    return <Navigate to={`/client/discover/${talentId}${search}`} replace />;
  }
  // Talent or other — fall back to their own profile page
  return <Navigate to="/talent/profile" replace />;
};

export default TalentProfileRedirect;
